package com.hyperether.goodjob.scenes

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.log
import com.hyperether.goodjob.models.Contact
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.JobFilter
import com.hyperether.goodjob.models.JobStatus
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.util.DataUtil
import com.hyperether.goodjob.util.DateTimeUtil
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

open class JobsViewModel(val repository: Repository) : ViewModel() {

    var tag = "JobsViewModel"
    val jobList = mutableStateListOf<Job>()
    val sitesMap = mutableStateMapOf<String, Site>()
    val filteredJobs = mutableStateListOf<Job>()

    val _teams = MutableStateFlow<List<Team>>(emptyList())
    val teams: StateFlow<List<Team>> = _teams

    val _sites = MutableStateFlow<List<Site>>(emptyList())
    val sites: StateFlow<List<Site>> = _sites

    private val _users = MutableStateFlow<List<User>>(emptyList())
    val users: StateFlow<List<User>> = _users

    val activeStatuses = setOf(
        JobStatus.open.name,
        JobStatus.in_progress.name,
        JobStatus.on_hold.name,
        JobStatus.pendingApproval.name
    )
    var selectedStatus = mutableStateOf(activeStatuses)
    var selectedEmployees = mutableStateOf(setOf<String>())
    var selectedTeams = mutableStateOf(setOf<String>())
    var selectedLocation = mutableStateOf(setOf<String>())
    var selectedSchedule = mutableStateOf(setOf<String>())
    val selectedJobs = mutableStateListOf<Job>()
    var jobClicked: MutableState<Boolean> = mutableStateOf(false)
    val selectedJobDashboard: MutableState<Job?> = mutableStateOf(null)
    val jobSites = mutableStateMapOf<String, Site>()

    var jobStatusMap: Map<String, String> = mutableMapOf()

    /**
     * This method will set initial flow to observe DB, or in case of web, will fetch data from remote
     * Should be called only once.
     * Use loadRemoteData() to fetch data from remote on demand
     */
    fun initViewModel() {
        viewModelScope.launch {
            repository.getTeamsFlow().collect {
                _teams.value = it
            }
        }

        viewModelScope.launch {
            repository.getJobsFlow().collect {
                log.d(tag) { "getJobsFlow reloadJobs" }
                reloadJobs(it)
            }
        }

        viewModelScope.launch {
            repository.getSitesFlow().collect { sites ->
                _sites.value = sites
                sitesMap.clear()
                sitesMap.putAll(sites.filter { site -> site.id != null }
                    .associateBy { site -> site.id!! })
            }
        }

        viewModelScope.launch {
            repository.getUsersFlow().collect {
                _users.value = it
                log.d(tag) { "getUsersFlow reloadJobs" }
                reloadJobs(jobList.map { job -> job.copy() })
            }
        }
    }

    open fun loadRemoteData() {
        repository.getToken().takeIf { it.isNotEmpty() }?.let {
            viewModelScope.launch {
                if (isEmployee()) {
                    repository.getAllJobsForUser()
                } else {
                    repository.getAllJobsForWorkspace()
                }
            }
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private val _contactsMap: StateFlow<Map<String, Contact>> = flow {
        emitAll(
            repository.getJobsFlow()
                .filter { it.isNotEmpty() }
                .mapLatest { jobs ->
                    jobs.mapNotNull { job ->
                        val jobId = job.id ?: return@mapNotNull null
                        val contactId = job.contactParentId ?: return@mapNotNull null
                        val contact = repository.getContact(contactId) ?: return@mapNotNull null
                        jobId to contact
                    }.toMap()
                }
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5000),
        initialValue = emptyMap()
    )

    val allContactsMap: StateFlow<Map<String, Contact>> = _contactsMap

    fun reloadJobs(jobs: List<Job>) {
        log.d(tag) { "JobsViewModel reloadJobs" }
        jobList.clear()
        // Enrich jobs with user data before adding
        val enrichedAssigneesJobs = jobs.map { job ->
            DataUtil.enrichJobAssigneesWithUsers(job, _users.value)
        }
        val enrichedJobs = enrichedAssigneesJobs.map { job ->
            DataUtil.enrichJobNotesWithUsers(job, _users.value)
        }
        jobList.addAll(enrichedJobs)
        if (jobList.isNotEmpty()) {
            val existingStatuses = jobList.mapNotNull { it.status }.toSet()
            selectedStatus.value = selectedStatus.value.intersect(existingStatuses)
        } else {
            selectedStatus.value = activeStatuses
        }
        filterJobs()
    }

    fun filterJobs() {
        filteredJobs.clear()
        filteredJobs.addAll(
            filterJobs(
                jobList,
                sitesMap,
                selectedStatus.value,
                selectedEmployees.value,
                selectedSchedule.value,
                selectedLocation.value
            )
        )
        log.d(tag) { "JobsViewModel filterJobs: ${filteredJobs.size} selectedStatus.value: ${selectedStatus.value}" }
    }

    open fun filterJobs(
        jobList: List<Job>,
        sitesMap: Map<String, Site>,
        selectedStatus: Set<String>,
        selectedEmployees: Set<String>,
        selectedSchedule: Set<String>,
        selectedLocation: Set<String>
    ): List<Job> {
        val filteredJobsList = mutableListOf<Job>()
        if (selectedStatus.isEmpty() &&
            selectedEmployees.isEmpty() &&
            selectedSchedule.isEmpty() &&
            selectedLocation.isEmpty()
        ) {
            filteredJobsList.addAll(jobList)
        } else {

            val selectedSitesIds = selectedLocation.flatMap { selectedAddress ->
                sitesMap.filterValues { site ->
                    site.addressObject?.address.equals(selectedAddress, ignoreCase = true)
                }.values.map { it.id }
            }
            filteredJobsList.addAll(jobList.filter { job ->
                selectedStatus.any { query ->
                    job.status?.equals(query, ignoreCase = true) == true
                }.or(selectedStatus.isEmpty()) && selectedEmployees.any { query ->
                    job.assignees?.employees?.any { employee ->
                        employee?.getName()?.equals(query, ignoreCase = true) == true
                    } == true || selectedEmployees.any { query ->
                        job.assignees?.teams?.any { team ->
                            team?.name?.equals(query, ignoreCase = true) == true
                        } == true
                    }
                }.or(selectedEmployees.isEmpty()) && selectedSchedule.any { query ->
                    job.startDate?.equals(query, ignoreCase = true) == true
                }.or(selectedSchedule.isEmpty()) && selectedSitesIds.equals(job.contactSiteId)
                    .or(selectedSitesIds.isEmpty())
            })
        }
        return filteredJobsList
    }

    fun getFilterMap(): Map<List<String>, Set<String>> {
        return getFilterMap(
            jobList = jobList,
            sitesMap = sitesMap,
            jobStatusMap = jobStatusMap,
            selectedStatus = selectedStatus.value,
            selectedEmployees = selectedEmployees.value,
            selectedLocation = selectedLocation.value,
            selectedSchedule = selectedSchedule.value
        )
    }

    fun getFilterMap(
        jobList: List<Job>,
        sitesMap: Map<String, Site>,
        jobStatusMap: Map<String, String>,
        selectedStatus: Set<String>,
        selectedEmployees: Set<String>,
        selectedLocation: Set<String>,
        selectedSchedule: Set<String>
    ): Map<List<String>, Set<String>> {
        val employeesList = jobList.flatMap { job ->
            job.assignees?.employees ?: emptyList()
        }
        val teamsList = jobList.flatMap { job ->
            job.assignees?.teams ?: emptyList()
        }
        val names = employeesList.map { it?.getName() ?: "" }
        val teamsName = teamsList.map { it?.name ?: "" }
        val employees = (names + teamsName).distinct()
        val contactAddresses = jobList.map { job ->
            sitesMap[job.id]?.addressObject?.address ?: ""
        }.distinct()
        val filterMap: MutableMap<List<String>, Set<String>> = mutableMapOf()
        val statuses = jobList.mapNotNull { job ->
            jobStatusMap.entries.find { it.value == job.status }?.key
        }.distinct()
        val checkedStatusList = selectedStatus.mapNotNull { status ->
            jobStatusMap.entries.find { it.value == status }?.key
        }.toSet()
        filterMap[statuses] = checkedStatusList.intersect(statuses.toSet())
        filterMap[employees] = selectedEmployees
        filterMap[contactAddresses] = selectedLocation
        filterMap[jobList.mapNotNull { it.startDate }.distinct()] = selectedSchedule
        return filterMap
    }

    fun applyFilter(map: Map<JobFilter, Set<String>>) {
        map.forEach { (key, values) ->
            when (key) {
                JobFilter.STATUS -> selectedStatus.value = values.map { entry ->
                    jobStatusMap[entry] ?: entry
                }.toSet()

                JobFilter.ASSIGNEE -> selectedEmployees.value = values
                JobFilter.LOCATION -> selectedLocation.value = values
                JobFilter.SCHEDULE -> selectedSchedule.value = values
                else -> println("Unknown filter: $key -> $values")
            }
        }
    }

    fun resetFilter() {
        selectedStatus.value = activeStatuses
        selectedEmployees.value = emptySet()
        selectedLocation.value = emptySet()
        selectedSchedule.value = emptySet()
    }

    fun searchJobs(jobSearchText: String): List<Job> {
        return searchJobs(jobSearchText, filteredJobs)
    }

    fun searchJobs(
        searchText: String,
        jobs: List<Job>
    ): List<Job> {
        val filteredJobsSearch = if (searchText.isNotBlank()) {
            jobs.filter { job ->
                job.jobTitle?.contains(searchText, ignoreCase = true) == true ||
                        job.contactSiteName?.contains(searchText, ignoreCase = true) == true
            }
        } else {
            jobs
        }
        return filteredJobsSearch
    }

    fun sortJobs(): List<Map.Entry<Comparable<LocalDate>, List<Job>>> {
        return sortJobs(filteredJobs)
    }

    fun sortJobs(jobs: List<Job>): List<Map.Entry<Comparable<LocalDate>, List<Job>>> {
        val (upcoming, past) = groupAndPartitionJobs(jobs, false) // get incomplete jobs
        val (upcomingDone, pastDone) = groupAndPartitionJobs(jobs, true) // get completed jobs

        val sortedUpcoming = upcoming.sortedBy { it.key } // soonest to latest
        val sortedPast = past.sortedByDescending { it.key } // most recent to oldest

        val sortedUpcomingDone = upcomingDone.sortedBy { it.key } // soonest to latest
        val sortedPastDone = pastDone.sortedByDescending { it.key } // most recent to oldest

        return sortedUpcoming + sortedPast + sortedUpcomingDone + sortedPastDone
    }

    private fun groupAndPartitionJobs(
        jobs: List<Job>,
        getCompleted: Boolean
    ): Pair<List<Map.Entry<LocalDate, List<Job>>>, List<Map.Entry<LocalDate, List<Job>>>> {
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
        return jobs
            .filter { (it.status == JobStatus.completed.name) == getCompleted }
            .groupBy { getJobDate(it, today) }
            .entries
            .partition { it.key >= today }
    }

    private fun getJobDate(job: Job, today: LocalDate): LocalDate {
        return if (job.startDate?.isNotEmpty() == true) {
            LocalDate.parse(job.startDate!!)
        } else {
            job.createdAt?.let { DateTimeUtil.extractDate(it) } ?: today
        }
    }

    fun getSortedJobList(filteredJobs: List<Job>): List<Job> {
        val sortedJobs = sortJobs(filteredJobs)
        val sortedJobList = sortedJobs.flatMap { it.value }
        return sortedJobList
    }

    fun searchAndSortJobs(jobSearchText: String): List<Map.Entry<Comparable<LocalDate>, List<Job>>> {
        val sortedGroupedJobs = sortJobs(searchJobs(jobSearchText))
        log.d("JobsViewModel") { "searchAndSortJobs: sortedGroupedJobs size: ${sortedGroupedJobs.size}" }
        return sortedGroupedJobs
    }

    fun isEmployee(): Boolean {
        return repository.getUser()?.role.equals(Role.employee.name, ignoreCase = true)
    }

    fun getUser(): User? {
        return repository.getUser()
    }

    fun updateSite(job: Job) {
        viewModelScope.launch {
            val siteMap = mutableMapOf<String, Site>()
            job.contactSiteId?.let { siteId ->
                val site = _sites.value.find { it.id == siteId }
                site?.let {
                    siteMap[job.id!!] = it
                }
            }
            jobSites.clear()
            jobSites.putAll(siteMap)
        }
    }

    private suspend fun getSiteById(id: String): Site? {
        return repository.getSiteById(id)
    }
}